/**
 * Brand Configuration
 *
 * Central brand config selected by VITE_BRAND env var at build time.
 * Usage: import { brand } from '../config/brand';
 *
 * Build commands:
 *   VITE_BRAND=autopilot  npx vite build   → Autopilot branded build
 *   VITE_BRAND=onbuzz     npx vite build   → OnBuzz branded build
 */

export interface BrandConfig {
  id: string;
  name: string;
  productName: string;
  fullProductName: string;
  tagline: string;
  domain: string;
  contactEmail: string;
  logoPath: string;
  /** Optional logo-text graphic (replaces text in header). Empty string = use text. */
  logoTextPath: string;
  faviconPath: string;
  storagePrefix: string;
  colors: {
    fire: string;
    fireSecondary: string;
    fireLight: string;
    fireDark: string;
  };
  /** RGBA versions for overlays (0-1 alpha appended by consumer) */
  colorsRgb: {
    fire: string;
    fireSecondary: string;
    fireLight: string;
    fireDark: string;
  };
  /** Hero section & page header gradients */
  gradients: {
    hero: string;
    heroHover: string;
    button: string;
    buttonHover: string;
    glow: string;
  };
  /** Text color for gradient buttons (white on dark accents, dark on bright accents) */
  buttonTextColor: string;
  /** DynamicBackground hue range (HSL hue values) */
  bgHues: {
    dark: number[];
    light: number[];
  };
  logoColors: {
    teal: string;
    orange: string;
    darkGray: string;
  };
  links: {
    npm: string;
    npmPackage: string;
    github: string;
    youtube: string;
    docs: string;
    website: string;
  };
  legal: {
    companyName: string;
    website: string;
  };
  installCommand: string;
}

const BRANDS: Record<string, BrandConfig> = {
  autopilot: {
    id: 'autopilot',
    name: 'Loxia Autopilot',
    productName: 'Autopilot',
    fullProductName: 'Loxia Autopilot',
    tagline: 'Break All Barriers',
    domain: 'autopilot.loxia.ai',
    contactEmail: 'contact@loxia.ai',
    logoPath: '/brands/autopilot/logo.webp',
    logoTextPath: '',
    faviconPath: '/brands/autopilot/favicon.svg',
    storagePrefix: 'loxia',
    // Metal gray / black / forged-metal-orange
    colors: {
      fire: '#c45a20',
      fireSecondary: '#d4702e',
      fireLight: '#e8935a',
      fireDark: '#9a4010',
    },
    colorsRgb: {
      fire: '196, 90, 32',
      fireSecondary: '212, 112, 46',
      fireLight: '232, 147, 90',
      fireDark: '154, 64, 16',
    },
    gradients: {
      hero: 'linear-gradient(135deg, #1f2937 0%, #374151 50%, #c45a20 100%)',
      heroHover: 'linear-gradient(135deg, #111827 0%, #1f2937 50%, #9a4010 100%)',
      button: 'linear-gradient(135deg, #c45a20 0%, #d4702e 100%)',
      buttonHover: 'linear-gradient(135deg, #9a4010 0%, #c45a20 100%)',
      glow: 'rgba(196, 90, 32, 0.35)',
    },
    buttonTextColor: '#ffffff',
    bgHues: {
      dark: [20, 25, 30, 15, 35],     // warm orange-brown spectrum
      light: [20, 25, 30, 35, 40],
    },
    logoColors: {
      teal: '#6b7280',
      orange: '#c45a20',
      darkGray: '#1f2937',
    },
    links: {
      npm: 'https://www.npmjs.com/package/@loxia-labs/loxia-autopilot-one',
      npmPackage: '@loxia-labs/loxia-autopilot-one',
      github: 'https://github.com/Loxia-ai/autopilot-releases',
      youtube: 'https://www.youtube.com/@LoxiaAI',
      docs: 'https://autopilot.loxia.ai',
      website: 'https://www.loxia.ai',
    },
    legal: {
      companyName: 'Loxia Labs LLC',
      website: 'https://www.loxia.ai',
    },
    installCommand: 'npm install -g @loxia-labs/loxia-autopilot-one',
  },

  onbuzz: {
    id: 'onbuzz',
    name: 'Loxia OnBuzz',
    productName: 'OnBuzz',
    fullProductName: 'Loxia OnBuzz',
    tagline: 'Your AI Fleet',
    domain: 'onbuzz.loxia.ai',
    contactEmail: 'contact@loxia.ai',
    logoPath: '/brands/onbuzz/logo.webp',
    logoTextPath: '/brands/onbuzz/logo-text.webp',
    faviconPath: '/brands/onbuzz/favicon.svg',
    storagePrefix: 'onbuzz',
    // Gentle/bright yellow + glass white
    colors: {
      fire: '#eab308',
      fireSecondary: '#facc15',
      fireLight: '#fde68a',
      fireDark: '#ca8a04',
    },
    colorsRgb: {
      fire: '234, 179, 8',
      fireSecondary: '250, 204, 21',
      fireLight: '253, 230, 138',
      fireDark: '202, 138, 4',
    },
    gradients: {
      hero: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #ca8a04 100%)',
      heroHover: 'linear-gradient(135deg, #0f0f1a 0%, #1a1a2e 50%, #a16207 100%)',
      button: 'linear-gradient(135deg, #eab308 0%, #facc15 100%)',
      buttonHover: 'linear-gradient(135deg, #ca8a04 0%, #eab308 100%)',
      glow: 'rgba(234, 179, 8, 0.35)',
    },
    buttonTextColor: '#1a1a2e',        // dark text on bright yellow buttons
    bgHues: {
      dark: [45, 48, 50, 42, 55],     // warm yellow spectrum
      light: [45, 48, 50, 52, 55],
    },
    logoColors: {
      teal: '#eab308',
      orange: '#facc15',
      darkGray: '#f8fafc',
    },
    links: {
      npm: '',
      npmPackage: 'onbuzz',
      github: '',
      youtube: 'https://www.youtube.com/@LoxiaAI',
      docs: 'https://onbuzz.loxia.ai',
      website: 'https://onbuzz.loxia.ai',
    },
    legal: {
      companyName: 'Loxia Labs LLC',
      website: 'https://www.loxia.ai',
    },
    installCommand: 'npm install -g onbuzz',
  },
};

// Select brand from env var, default to autopilot
const brandId = import.meta.env.VITE_BRAND || 'autopilot';
export const brand: BrandConfig = BRANDS[brandId] || BRANDS.autopilot;

export default brand;
